import * as React from "react";
import { Dialog } from "@ui/core";
import { Keyboard } from "lucide-react";
import { useTranslation } from "react-i18next";

interface ShortcutHelpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ShortcutHelpModal({
  open,
  onOpenChange,
}: ShortcutHelpModalProps) {
  const { t } = useTranslation("common");

  const globalShortcuts = [
    {
      keys: ["Ctrl", "K"],
      description: t("shortcuts.focusSearch", "Focus Search"),
    },
    { keys: ["Ctrl", "/"], description: t("shortcuts.help", "Shortcut Help") },
    { keys: ["Esc"], description: t("shortcuts.close", "Close Modals/Cancel") },
  ];

  const formShortcuts = [
    {
      keys: ["Ctrl", "S"],
      description: t("shortcuts.saveChanges", "Save Changes"),
    },
  ];

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("shortcuts.title", "Keyboard Shortcuts")}
      description={t(
        "shortcuts.description",
        "Quickly navigate and perform actions using keyboard shortcuts.",
      )}
    >
      <div className="space-y-6">
        <div>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Keyboard className="h-4 w-4" />
            {t("shortcuts.global", "Global Shortcuts")}
          </h3>
          <div className="space-y-2">
            {globalShortcuts.map((shortcut) => (
              <div
                key={shortcut.description}
                className="flex items-center justify-between text-sm"
              >
                <span>{shortcut.description}</span>
                <div className="flex gap-1">
                  {shortcut.keys.map((key) => (
                    <kbd
                      key={key}
                      className="inline-flex items-center justify-center rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium opacity-100"
                    >
                      {key}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h3 className="mb-3 flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Keyboard className="h-4 w-4" />
            {t("shortcuts.form", "Form Shortcuts")}
          </h3>
          <div className="space-y-2">
            {formShortcuts.map((shortcut) => (
              <div
                key={shortcut.description}
                className="flex items-center justify-between text-sm"
              >
                <span>{shortcut.description}</span>
                <div className="flex gap-1">
                  {shortcut.keys.map((key) => (
                    <kbd
                      key={key}
                      className="inline-flex items-center justify-center rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium opacity-100"
                    >
                      {key}
                    </kbd>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Dialog>
  );
}
